import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Application, Theme, SequenceStatus } from '../types';
import { FolderOpen, Moon, Sun, Cloud, RefreshCcw, FileCheck, FileUp } from 'lucide-react';
import { UserProfile } from './UserProfile';
import { SharePointPicker } from './SharePointPicker';
import { isSharePointEnabled } from '../services/sharepoint-service';
import './Toolbar.css';

interface ToolbarProps {
  onFileSelect: (files: FileList) => void;
  onSequenceChange: (sequenceId: string) => void;
  onThemeToggle: () => void;
  onReset: () => void;
  application: Application | null;
  currentSequence: string | null;
  theme: Theme;
  onSignOff?: () => void;
  onExportComments?: () => void;
  commentCount?: number;
  sequenceStatus?: SequenceStatus;
}

const SEQUENCE_STATUS_LABELS: Record<SequenceStatus, string> = {
  not_reviewed: 'Not Reviewed',
  in_review: 'In Review',
  review_completed: 'Review Completed',
  approved: 'Approved',
};

export function Toolbar({
  onFileSelect,
  onSequenceChange,
  onThemeToggle,
  onReset,
  application,
  currentSequence,
  theme,
  onSignOff,
  onExportComments,
  commentCount = 0,
  sequenceStatus = 'not_reviewed',
}: ToolbarProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showSharePointPicker, setShowSharePointPicker] = useState(false);
  const sharePointEnabled = false && isSharePointEnabled();

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      onFileSelect(files);
    }
  };

  const handleSequenceChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    onSequenceChange(event.target.value);
  };

  return (
    <header className="toolbar">
      <div className="toolbar-left">
        <div className="brand">
          <img src="/logo.png" alt="Logo" className="brand-logo" />
          <div className="toolbar-logo">
            <span>eCTDPro Viewer</span>
          </div>
        </div>
        <button
          className="toolbar-button open-button"
          onClick={() => fileInputRef.current?.click()}
          title="Open eCTD folder from computer"
        >
          <FolderOpen className="icon" />
          <span>Open Local</span>
        </button>
        
        {sharePointEnabled && (
          <button
            className="toolbar-button sharepoint-button"
            onClick={() => setShowSharePointPicker(true)}
            title="Load eCTD from SharePoint"
          >
            <Cloud className="icon" />
            <span>SharePoint</span>
          </button>
        )}
        
        <button
          className="toolbar-button reset-button"
          onClick={() => {
            setShowSharePointPicker(false);
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
            onReset();
          }}
          title="Reset viewer"
          aria-label="Reset viewer"
          disabled={!application}
        >
          <RefreshCcw className="icon" />
        </button>
        
        {application && (
          <>
            <div className="toolbar-group">
              <label className="toolbar-label">Application:</label>
              <select 
                className="toolbar-select"
                value={application.id}
                disabled
              >
                <option value={application.id}>{application.id}</option>
              </select>
            </div>

            <div className="toolbar-group">
              <label className="toolbar-label">Sequence:</label>
              <select
                className="toolbar-select"
                value={currentSequence || ''}
                onChange={handleSequenceChange}
              >
                {application.sequences.map(seq => (
                  <option key={seq.id} value={seq.id}>
                    {seq.id}
                  </option>
                ))}
              </select>
            </div>

            <button
              className={`sequence-status-chip ${sequenceStatus}`}
              onClick={onSignOff}
              title={`Sequence review status: ${SEQUENCE_STATUS_LABELS[sequenceStatus]} - click to view or add sign-offs`}
            >
              <span className="sequence-status-dot" />
              {SEQUENCE_STATUS_LABELS[sequenceStatus]}
            </button>
          </>
        )}
      </div>

      <div className="toolbar-center">
      </div>

      <div className="toolbar-right">
        {application && onSignOff && (
          <button
            className="toolbar-button icon-action-button signoff-button"
            onClick={onSignOff}
            title="Sign-off: electronic review sign-off for the current sequence"
            aria-label="Review sign-off"
          >
            <FileCheck className="icon" />
          </button>
        )}

        {application && onExportComments && (
          <button
            className="toolbar-button icon-action-button export-comments-button"
            onClick={onExportComments}
            title={commentCount > 0 ? `Export ${commentCount} review comment(s) to Excel` : 'No review comments to export yet'}
            aria-label="Export review comments"
            disabled={commentCount === 0}
          >
            <FileUp className="icon" />
          </button>
        )}

        <button
          className="toolbar-button theme-toggle-button"
          onClick={onThemeToggle}
          title={`Switch to ${theme.name === 'light' ? 'dark' : 'light'} theme`}
        >
          {theme.name === 'light' ? <Moon className="icon" strokeWidth={2} /> : <Sun className="icon" strokeWidth={2} />}
        </button>

        <UserProfile onLogout={() => window.location.reload()} />

        <input
          ref={fileInputRef}
          id="file-input"
          type="file"
          multiple
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
          {...({ webkitdirectory: '' } as any)}
        />
      </div>
      
      {sharePointEnabled && showSharePointPicker && typeof document !== 'undefined' &&
        createPortal(
          <SharePointPicker
            onFilesSelected={onFileSelect}
            onClose={() => setShowSharePointPicker(false)}
          />,
          document.body
        )}
    </header>
  );
}

